/**
 * Draggable calendar event chip. Reads channel color for the accent stripe.
 */
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import type { CalendarEvent } from "@/entities/mission-control";
import { PUBLISHING_TONES } from "@/entities/mission-control";

interface EventChipProps {
  event: CalendarEvent;
  onOpen: (e: CalendarEvent) => void;
  compact?: boolean;
  timeLabel?: string;
}

export function EventChip({ event, onOpen, compact = false, timeLabel }: EventChipProps): JSX.Element {
  const { setNodeRef, listeners, attributes, transform, isDragging } = useDraggable({
    id: event.id,
    data: { event },
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    borderLeft: `3px solid ${event.channel_color}`,
    cursor: "grab",
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      data-testid={`mc-event-${event.id}`}
      onClick={() => onOpen(event)}
      className={`w-full text-left rounded-md bg-card px-2 ${compact ? "py-1" : "py-1.5"} shadow-sm ring-1 ring-border/60 transition hover:ring-primary/60 hover:shadow-md`}
      {...listeners}
      {...attributes}
    >
      <div className="flex items-center gap-1.5">
        <span
          data-testid={`mc-event-channel-${event.id}`}
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: event.channel_color }}
          aria-label={event.channel_label}
        />
        <span className="truncate text-[11px] font-medium text-foreground">
          {timeLabel ? <span className="mr-1 font-mono text-muted-foreground">{timeLabel}</span> : null}
          {event.asset_title}
        </span>
      </div>
      {!compact ? (
        <div className="mt-1 flex items-center justify-between gap-1">
          <span
            data-testid={`mc-event-status-${event.id}`}
            className={`inline-flex items-center rounded-full px-1.5 py-px text-[9px] font-medium uppercase tracking-wide ${PUBLISHING_TONES[event.publishing_status]}`}
          >
            {event.publishing_status_label}
          </span>
          <span className="font-mono text-[9px] text-muted-foreground">
            {event.permanent_asset_id ?? event.channel_label}
          </span>
        </div>
      ) : null}
    </button>
  );
}
